import dayjs from 'dayjs'

const MEXICO_TIME_ZONE = 'America/Mexico_City'

export const getCommentDate = (item: any) =>
  item?.createdAt ?? item?.value?.createdAt ?? item?.label?.createdAt

export const getCommentText = (item: any) =>
  item?.comment ||
  item?.value?.comment ||
  item?.label?.comment ||
  ''

export const getCommentScore = (item: any) =>
  Number(item?.score ?? item?.value?.score ?? item?.label?.score)

export const toMexicoDateTime = (value?: string) => {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MEXICO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(date)

  const get = (type: string) => parts.find(part => part.type === type)?.value ?? '00'
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`
}

export const formatCommentDate = (value?: string, pattern = 'DD/MM/YYYY') => {
  const mexicoDate = toMexicoDateTime(value)
  if (!mexicoDate) {
    return ''
  }

  const [datePart, timePart = '00:00:00'] = mexicoDate.split('T')
  const [year, month, day] = datePart.split('-')
  const [hour, minute] = timePart.split(':')

  if (pattern === 'DD/MM/YYYY HH:mm') {
    return `${day}/${month}/${year} ${hour}:${minute}`
  }

  return `${day}/${month}/${year}`
}

const isCommentInDateRange = (
  item: any,
  fromDate?: string,
  toDate?: string
) => {
  if (!fromDate && !toDate) {
    return true
  }

  const commentDate = dayjs(toMexicoDateTime(getCommentDate(item)))
  if (!commentDate.isValid()) {
    return false
  }

  if (fromDate && commentDate.isBefore(dayjs(fromDate).startOf('day'))) {
    return false
  }

  if (toDate && commentDate.isAfter(dayjs(toDate).endOf('day'))) {
    return false
  }

  return true
}

const toCommentsArray = (data: any): any[] => {
  if (!data) {
    return []
  }

  if (typeof data.asMutable === 'function') {
    return data.asMutable()
  }

  if (Array.isArray(data)) {
    return data
  }

  return data?.data || data?.list || []
}

export const rankCommentsByScore = (
  data: any,
  {
    type,
    limit,
    fromDate,
    toDate,
    preferExtreme = false
  }: {
    type?: 'positive' | 'negative'
    limit?: number
    fromDate?: string
    toDate?: string
    preferExtreme?: boolean
  } = {}
) => {
  const filteredList = toCommentsArray(data).filter((item: any) => {
    const score = getCommentScore(item)
    const tipo = String(
      item?.type || item?.value?.type || item?.label?.type || ''
    ).toLowerCase().trim()

    const matchType = (() => {
      if (!type) {
        return true
      }

      if (!isNaN(score)) {
        return type === 'negative' ? score < 0 : score > 0
      }

      return tipo === type
    })()

    return matchType && isCommentInDateRange(item, fromDate, toDate)
  })

  const uniqueMap = new Map()

  filteredList.forEach((item: any) => {
    const id =
      item?.surveyResponseId ||
      item?.value?.surveyResponseId ||
      item?.label?.surveyResponseId

    if (id && !uniqueMap.has(id)) {
      uniqueMap.set(id, item)
    } else if (!id) {
      uniqueMap.set(`row-${uniqueMap.size}`, item)
    }
  })

  const uniqueList = Array.from(uniqueMap.values())

  const extremeScoreList = preferExtreme
    ? uniqueList.filter((item: any) => {
      const score = getCommentScore(item)
      if (isNaN(score)) {
        return false
      }

      return type === 'negative' ? score <= -100 : score >= 100
    })
    : []

  const rankedList = (extremeScoreList.length > 0 ? extremeScoreList : uniqueList)
    .sort((itemA: any, itemB: any) => {
      const scoreA = getCommentScore(itemA)
      const scoreB = getCommentScore(itemB)
      const scoreDiff = type === 'negative'
        ? scoreA - scoreB
        : scoreB - scoreA

      if (scoreDiff !== 0) {
        return scoreDiff
      }

      return getCommentText(itemB).length - getCommentText(itemA).length
    })

  const slicedList = limit ? rankedList.slice(0, limit) : rankedList

  return slicedList.map((item: any) => ({
    ...item,
    comment: getCommentText(item) || 'Sin comentario',
    createdAt: toMexicoDateTime(getCommentDate(item))
  }))
}

export const buildCommentsMapper = ({
  type,
  limit = 5,
  fromDate,
  toDate,
  preferExtreme = true
}: {
  type?: 'positive' | 'negative'
  limit?: number
  fromDate?: string
  toDate?: string
  preferExtreme?: boolean
} = {}) => {
  return (data: any) => rankCommentsByScore(data, {
    type,
    limit,
    fromDate,
    toDate,
    preferExtreme
  })
}

const WORD_CLOUD_STOP_WORDS = new Set([
  'para', 'como', 'esta', 'este', 'esto', 'estos', 'estas', 'pero', 'porque',
  'tiene', 'tienen', 'todo', 'todos', 'toda', 'todas', 'muy', 'mas',
  'que', 'los', 'las', 'del', 'una', 'uno', 'unos', 'unas', 'con', 'por',
  'sin', 'sobre', 'entre', 'cuando', 'donde', 'quien', 'cual', 'cuales',
  'hay', 'ser', 'son', 'fue', 'era', 'han', 'hace', 'hacer', 'puede',
  'bien', 'mal', 'solo', 'tambien', 'asi', 'aqui', 'alli', 'ya', 'si',
  'no', 'ni', 'al', 'el', 'la', 'lo', 'de', 'en', 'un', 'se', 'es',
  'me', 'te', 'le', 'su', 'mi', 'tu', 'nos', 'les', 'sus', 'esa',
  'ese', 'eso', 'otro', 'otra', 'otros', 'otras', 'mismo', 'misma',
  'siempre', 'nunca', 'ahora', 'despues', 'antes', 'entonces', 'aunque'
])

const normalizeCloudWord = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

export const commentContainsWord = (item: any, word?: string) => {
  if (!word) {
    return true
  }

  const comment = normalizeCloudWord(getCommentText(item))
  const needle = normalizeCloudWord(word)
  return needle.length > 0 && comment.includes(needle)
}

export const buildWordCloudFromComments = (
  comments: any[],
  maxResults = 30
) => {
  const counts = new Map<string, number>()

  toCommentsArray(comments).forEach((item: any) => {
    getCommentText(item)
      .split(/[.,!?:;'"()\-\s]+/)
      .forEach((raw: string) => {
        const word = normalizeCloudWord(raw)
        if (word.length < 5 || WORD_CLOUD_STOP_WORDS.has(word) || /^\d+$/.test(word)) {
          return
        }

        counts.set(word, (counts.get(word) || 0) + 1)
      })
  })

  return Array.from(counts.entries())
    .map(([group, count]) => ({
      group: group.charAt(0).toUpperCase() + group.slice(1),
      count,
      value: count
    }))
    .sort((itemA, itemB) => itemB.count - itemA.count)
    .slice(0, maxResults)
}
